import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { ArrowsClockwise, Plus, Sparkle, Trash } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  in_progress: "bg-amber-100 text-amber-800 border-amber-300",
  ready: "bg-blue-100 text-blue-800 border-blue-300",
  complete: "bg-green-100 text-green-800 border-green-300",
};

export default function StarLanding() {
  const [rows, setRows] = useState(null);
  const [stds, setStds] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new"); // "new" = no predecessor; "transition" = current -> future
  const [curId, setCurId] = useState("");
  const [futId, setFutId] = useState("");
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { canEdit } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/transitions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/standards").then((r) => setStds(r.data || [])).catch(() => setStds([]));
  }, []);

  const del = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/transitions/${delTarget.id}`);
      toast.success("STaR deleted");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete STaR");
    } finally { setDeleting(false); }
  };

  // Dropdown candidates. Kept permissive so an SME can run OLDER / already-effective
  // versions to play catch-up: the target may already be enforceable, and the
  // predecessor may already be retired.
  const futureStds = stds.filter((s) => s.status !== "retired");
  const currentStds = stds;

  const create = async () => {
    if (!futId) return toast.error("Select the standard you're preparing for");
    if (mode === "transition" && !curId) return toast.error("Select the current predecessor");
    setBusy(true);
    try {
      const payload = { future_standard_id: futId };
      if (mode === "transition") payload.current_standard_id = curId;
      const { data } = await api.post("/transitions", payload);
      toast.success(mode === "new" ? "New-standard STaR created" : "Transition STaR created");
      // Kick off AI narrative generation for new-standard mode
      if (mode === "new") {
        try {
          const r = await api.post(`/transitions/${data.id}/generate-narratives`);
          toast.success(`AI queued ${r.data.queued} narrative${r.data.queued === 1 ? "" : "s"}`);
        } catch { /* narrative gen failure isn't fatal to the STaR */ }
      }
      setOpen(false);
      setCurId("");
      setFutId("");
      nav(`/transitions/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create STaR");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <PageHeader title="STaR — Standards Tracking & Transitions"
          subtitle="Track standard version transitions AND populate readiness workspaces for brand-new NERC standards. AI drafts a 'what good looks like' narrative for every new requirement." />
        <Button data-testid="star-new-btn" onClick={() => setOpen(true)} className="shrink-0">
          <Plus size={16} className="mr-1.5" /> New STaR
        </Button>
      </div>
      {rows === null ? null : rows.length === 0 ? (
        <Empty title="No STaRs yet"
          hint="Click 'New STaR' to create a readiness workspace for a brand-new standard — AI will draft the implementation narrative for every requirement." />
      ) : (
        <div className="border border-border" data-testid="star-list">
          <table className="text-sm w-full">
            <thead className="bg-secondary">
              <tr>{["STaR", "From → To", "Type", "Status", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>))}</tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                  data-testid={`star-row-${t.id}`} onClick={() => nav(`/transitions/${t.id}`)}>
                  <td className="px-4 py-3 font-medium">{t.name || "STaR"}</td>
                  <td className="px-4 py-3">
                    {t.is_new_standard ? (
                      <span className="text-xs"><span className="text-muted-foreground">— (new) →</span> <span className="font-mono">{t.future_standard_id}</span></span>
                    ) : (
                      <span className="text-xs font-mono">{t.current_standard_id || "—"} → {t.future_standard_id || "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.is_new_standard ? (
                      <span className="text-xs border border-emerald-300 bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                        <Sparkle size={10} weight="fill" /> new standard
                      </span>
                    ) : (
                      <span className="text-xs border border-blue-300 bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded">transition</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs border px-1.5 py-0.5 rounded ${STATUS_STYLE[t.status] || STATUS_STYLE.draft}`}>
                      {(t.status || "draft").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <button
                          data-testid={`star-delete-${t.id}`}
                          onClick={(e) => { e.stopPropagation(); setDelTarget(t); }}
                          title="Delete STaR"
                          className="text-muted-foreground hover:text-red-600 transition-colors">
                          <Trash size={16} />
                        </button>
                      )}
                      <ArrowsClockwise size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="star-new-dialog">
          <DialogHeader><DialogTitle>New STaR readiness workspace</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Type</Label>
              <div className="flex gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="star-mode" value="new"
                    data-testid="star-mode-new" checked={mode === "new"} onChange={() => setMode("new")} />
                  <span className="text-sm">New standard (no predecessor)</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="star-mode" value="transition"
                    data-testid="star-mode-transition" checked={mode === "transition"} onChange={() => setMode("transition")} />
                  <span className="text-sm">Transition (current → future)</span>
                </label>
              </div>
            </div>

            {mode === "transition" && (
              <div>
                <Label htmlFor="star-current" className="mb-1 block">Current / predecessor standard</Label>
                <Select value={curId} onValueChange={setCurId}>
                  <SelectTrigger data-testid="star-current-select"><SelectValue placeholder="Select current standard…" /></SelectTrigger>
                  <SelectContent>
                    {currentStds.map((s) => (
                      <SelectItem key={s.standard_id} value={s.standard_id}>
                        {s.standard_id} — {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="star-future" className="mb-1 block">
                {mode === "new" ? "Standard you're preparing for" : "Future / target standard"}
              </Label>
              <Select value={futId} onValueChange={setFutId}>
                <SelectTrigger data-testid="star-future-select"><SelectValue placeholder="Select standard…" /></SelectTrigger>
                <SelectContent>
                  {(mode === "new" ? futureStds : stds).map((s) => (
                    <SelectItem key={s.standard_id} value={s.standard_id}>
                      {s.standard_id} — {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === "new" && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  AI will draft a ~1000-word &ldquo;what good looks like&rdquo; narrative for every requirement — purpose, expectations, expected controls, evidence, common gaps, and first-90-days actions.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="star-create-btn" onClick={create} disabled={busy || !futId}>
              {busy ? "Creating…" : "Create STaR"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-md" data-testid="star-delete-dialog">
          <DialogHeader><DialogTitle>Delete this STaR?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            &ldquo;<span className="font-medium text-foreground">{delTarget?.name || "STaR"}</span>&rdquo; and all of its
            rows, narratives, and attached sources will be permanently removed for this entity. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} disabled={deleting}>Cancel</Button>
            <Button data-testid="star-delete-confirm" onClick={del} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Deleting…" : "Delete STaR"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
